import React, { useEffect, useRef, useState } from 'react';
import { Check, Copy, Mic2, Minus, Plus, X } from 'lucide-react';
import { vibeStyle } from '../../utils/vibeStyle';
import { buildStyleString } from '../../utils/reflectEditor';
import {
  bpmFromStyle,
  cadenceText,
  fullSongSheet,
  songSheetLyrics,
  styleChips,
} from '../../utils/reflectFlow';

// Clipboard with a fallback for older classroom browsers and non-secure pages.
const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (_error) {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(area);
    return ok;
  }
};

const CopyButton = ({ label, text, copied, onCopied, onFirstCopy, big = false }) => (
  <button
    type="button"
    onClick={async () => {
      if (await copyText(text)) {
        onCopied();
        onFirstCopy();
      }
    }}
    className={`booth-copy neon-btn inline-flex items-center justify-center gap-2 rounded-full font-black
      text-[#0B0B0C] transition-transform hover:scale-[1.03] focus:outline-none focus-visible:ring-2
      focus-visible:ring-white ${big ? 'min-h-14 w-full px-6 text-lg' : 'min-h-11 px-5 text-base'}
      ${copied ? 'is-copied' : ''}`}
    style={{ background: 'linear-gradient(135deg, var(--glow), color-mix(in srgb, var(--glow) 55%, white))' }}
  >
    {copied ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
    {copied ? 'Copied' : label}
  </button>
);

const BoothCard = ({ kicker, children, copy }) => (
  <div className="booth-card relative flex flex-col rounded-3xl border border-white/10 p-5">
    <p className="mb-3 text-xs font-black uppercase tracking-[0.18em]" style={{ color: 'var(--glow)' }}>
      {kicker}
    </p>
    <div className="flex-1">{children}</div>
    <div className="mt-5">{copy}</div>
  </div>
);

const BoothSheet = ({
  cleanRhymes = true,
  draft,
  kicker = 'Take it to the booth',
  onChange,
  onFirstCopy,
}) => {
  const { Icon, color } = vibeStyle(draft.vibe || '');
  const bpm = bpmFromStyle(draft.style) || 90;
  const chips = styleChips(draft.style);
  const lyrics = songSheetLyrics(draft);
  const lyricLines = lyrics.split('\n');
  const [copied, setCopied] = useState('');
  const [chipText, setChipText] = useState('');
  const [editingFlow, setEditingFlow] = useState('');
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const markCopied = (key) => () => {
    setCopied(key);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(''), 1800);
  };

  const linesBySection = Object.fromEntries(
    (draft.sections || []).map((section) => [section.label, (section.lines || []).length]),
  );

  const updateStyle = (nextChips, nextBpm = bpm) => {
    onChange({
      type: 'edit_style',
      style: buildStyleString(nextChips, nextBpm, cleanRhymes),
    });
  };

  const addChip = (event) => {
    event.preventDefault();
    const clean = chipText.trim();
    if (!clean) return;
    updateStyle([...chips, clean]);
    setChipText('');
  };

  useEffect(() => {
    const normalized = buildStyleString(chips, bpm, cleanRhymes);
    if (normalized !== draft.style) onChange({ type: 'edit_style', style: normalized });
  }, [bpm, chips, cleanRhymes, draft.style, onChange]);

  return (
    <section
      aria-label="Your song sheet"
      style={{ '--glow': color, '--beat': `${bpm ? 60 / bpm : 0.6}s` }}
      className="booth neon-card loop-card relative mt-8 overflow-hidden rounded-[2rem] border
        border-white/10 p-5 sm:p-8"
    >
      <div aria-hidden="true" className="loop-card-wash pointer-events-none absolute -right-24 -top-28
        h-80 w-80 rounded-full blur-3xl" style={{ backgroundColor: color }} />
      <Icon aria-hidden="true" className="pointer-events-none absolute -right-6 top-6 h-40 w-40
        rotate-[-12deg] opacity-[0.06]" style={{ color }} />

      <div className="relative flex flex-wrap items-end justify-between gap-6">
        <div className="min-w-0">
          <p className="flex items-center gap-2 text-sm font-black uppercase tracking-[0.2em]"
            style={{ color }}>
            <Mic2 className="h-4 w-4" /> {kicker}
          </p>
          <h2 className="mt-2 font-heading text-3xl font-black sm:text-4xl">Your song sheet</h2>
          <p className="mt-1 max-w-md text-base text-white/60">
            Style, flow and lyrics, ready to paste into your song tool.
          </p>
        </div>
        <div className="booth-tempo flex items-center gap-3 rounded-3xl border border-white/10 px-3 py-3"
          aria-label={`Tempo ${bpm} BPM`}>
          <button
            type="button"
            onClick={() => updateStyle(chips, Math.max(50, bpm - 1))}
            disabled={bpm <= 50}
            className="flex min-h-11 min-w-11 items-center justify-center rounded-full border
              border-white/15 hover:border-white/40 disabled:opacity-35"
            aria-label="Lower BPM"
          >
            <Minus className="h-4 w-4" />
          </button>
          <div>
            <p className="font-heading text-5xl font-black leading-none" style={{ color }}>{bpm}</p>
            <p className="text-xs font-black uppercase tracking-[0.2em] text-white/50">BPM</p>
          </div>
          <button
            type="button"
            onClick={() => updateStyle(chips, Math.min(200, bpm + 1))}
            disabled={bpm >= 200}
            className="flex min-h-11 min-w-11 items-center justify-center rounded-full border
              border-white/15 hover:border-white/40 disabled:opacity-35"
            aria-label="Raise BPM"
          >
            <Plus className="h-4 w-4" />
          </button>
          <div className="hidden gap-1.5 sm:flex" aria-hidden="true">
            {[0, 1, 2, 3].map((beat) => (
              <span key={beat} className="booth-beat h-3 w-3 rounded-full"
                style={{ animationDelay: `calc(var(--beat) * ${beat})` }} />
            ))}
          </div>
        </div>
      </div>

      <div className="relative mt-6">
        <CopyButton big label="Copy everything" text={fullSongSheet(draft)}
          copied={copied === 'all'} onCopied={markCopied('all')} onFirstCopy={onFirstCopy} />
      </div>

      <div className="relative mt-6 grid gap-4 lg:grid-cols-3">
        <BoothCard
          kicker="Style"
          copy={<CopyButton label="Copy style" text={draft.style || ''}
            copied={copied === 'style'} onCopied={markCopied('style')} onFirstCopy={onFirstCopy} />}
        >
          <div className="flex flex-wrap gap-2">
            {chips.map((chip) => (
              <span key={chip} className="booth-chip inline-flex min-h-11 items-center rounded-full
                border pl-3 text-base font-bold">
                {chip}
                <button
                  type="button"
                  onClick={() => updateStyle(chips.filter((item) => item !== chip))}
                  className="flex min-h-11 min-w-11 items-center justify-center rounded-full"
                  aria-label={`Remove ${chip}`}
                >
                  <X className="h-4 w-4" />
                </button>
              </span>
            ))}
            {cleanRhymes && (
              <span className="booth-chip inline-flex min-h-11 items-center rounded-full border px-3
                text-base font-bold">
                clean lyrics
              </span>
            )}
          </div>
          <form onSubmit={addChip} className="mt-3 flex gap-2">
            <label htmlFor="new-style-chip" className="sr-only">Add a style</label>
            <input
              id="new-style-chip"
              value={chipText}
              onChange={(event) => setChipText(event.target.value)}
              maxLength={60}
              placeholder="Add a style"
              className="min-h-11 min-w-0 flex-1 rounded-full border border-white/15 bg-black/25
                px-3 text-base text-white outline-none focus:border-[color:var(--glow)]"
            />
            <button
              type="submit"
              disabled={!chipText.trim()}
              className="min-h-11 rounded-full border border-white/15 px-3 text-base font-black
                hover:border-[color:var(--glow)] disabled:opacity-40"
            >
              + add
            </button>
          </form>
        </BoothCard>

        <BoothCard
          kicker="Flow"
          copy={<CopyButton label="Copy flow" text={cadenceText(draft)}
            copied={copied === 'flow'} onCopied={markCopied('flow')} onFirstCopy={onFirstCopy} />}
        >
          <div className="space-y-3">
            {(draft.sections || []).map((section) => {
              const item = (draft.cadence || []).find((note) => note.label === section.label);
              const isEditing = editingFlow === section.label;
              return (
              <div key={section.label} className={!item ? 'booth-flow-empty' : ''}>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-black uppercase tracking-wide text-white">
                    {section.label}
                  </span>
                  <span className="flex flex-1 gap-1" aria-hidden="true">
                    {Array.from({ length: Math.max(1, linesBySection[section.label] || 1) }).map((_, bar) => (
                      <span key={bar} className="booth-bar h-2 flex-1 rounded-full" />
                    ))}
                  </span>
                </div>
                {item || isEditing ? (
                  <input
                    value={item?.note || ''}
                    onChange={(event) => onChange({
                      type: 'edit_cadence',
                      label: section.label,
                      note: event.target.value,
                    })}
                    onFocus={() => setEditingFlow(section.label)}
                    onBlur={() => setEditingFlow('')}
                    maxLength={160}
                    autoFocus={isEditing && !item}
                    placeholder="How should this section flow?"
                    className="mt-2 min-h-11 w-full rounded-xl border border-white/10 bg-black/20
                      px-3 text-base text-white outline-none focus:border-[color:var(--glow)]"
                    aria-label={`${section.label} flow note`}
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => setEditingFlow(section.label)}
                    className="mt-1 min-h-11 text-left text-base font-bold text-white/55
                      hover:text-white"
                  >
                    + Add flow note
                  </button>
                )}
              </div>
              );
            })}
          </div>
        </BoothCard>

        <BoothCard
          kicker="Lyrics"
          copy={<CopyButton label="Copy lyrics" text={lyrics}
            copied={copied === 'lyrics'} onCopied={markCopied('lyrics')} onFirstCopy={onFirstCopy} />}
        >
          <div className="booth-lyrics relative max-h-44 overflow-hidden font-mono text-sm leading-relaxed">
            {lyricLines.map((line, index) => (
              <p key={`${line}-${index}`}
                className={/^\[.*\]$/.test(line) ? 'font-black' : 'text-white/75'}
                style={/^\[.*\]$/.test(line) ? { color } : undefined}>
                {line || ' '}
              </p>
            ))}
          </div>
        </BoothCard>
      </div>
    </section>
  );
};

export default BoothSheet;
